import {
  sortByDateTime,
  LaunchPage,
  MaterialUiTable,
  usePersistMuiTableState,
  MaterialUiColumnsProps,
  Tag,
  Avatar,
  Spacer,
} from "@qivia/ui";
import { useTranslation } from "react-i18next";
import { PublicEmspCdrsDisplayed, PublicEmspCdrs } from "../transactionsAPI";
import { useAppDispatch, useAppSelector } from "../../../../redux/hooks";
import {
  selectEmspCdrsList,
  selectEmspCdrsListStatus,
} from "../transactionsSlice";
import { useCallback, useEffect, useMemo, useState } from "react";
import { documentDownloadXlsxAsync } from "../../../../libSlice/downloadFileXslx";
import {
  GRID_AGGREGATION_ROOT_FOOTER_ROW_ID,
  useGridApiRef,
} from "@mui/x-data-grid-premium";
import {
  formatCurrency,
  formatDateTime,
  formatHour,
  getDateTime,
} from "../../../../libSlice/materialUiFormats";
import "../../../../i18n/i18n";
import { useNavigate, useParams } from "react-router-dom";
import styled from "styled-components";
import { authorizationStatusProps } from "../lib";

interface SharedInterfaceCardTransactionsProps {
  sidePanelAction?: (props: Partial<PublicEmspCdrs>) => void;
  sidePanel?: JSX.Element;
}

export const SharedInterfaceEmspCdrs = (
  props: SharedInterfaceCardTransactionsProps,
) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const params = useParams();
  const [listDisplayed, setListDisplayed] = useState<
    PublicEmspCdrsDisplayed[] | null
  >(null);
  const emspCdrsList: PublicEmspCdrs[] | null =
    useAppSelector(selectEmspCdrsList);
  const emspCdrsListStatus = useAppSelector(selectEmspCdrsListStatus);
  const [rowHoverUuid, setRowHoverUuid] = useState<string | null>(null);

  const pageName = "emspCdrs";
  const apiRef = useGridApiRef();
  const key = "customers-grid";

  const aggregation = {
    model: {
      startAt: "size",
      totalCost: "avg",
      totalEnergy: "avg",
    },
  };

  const dataGridState = usePersistMuiTableState(
    apiRef,
    key,
    pageName,
    aggregation,
  );

  const findKeysToTranslate = useCallback(
    (key: keyof PublicEmspCdrs, value: string) => {
      switch (key) {
        case "authorizationStatus":
          return t(`emspCdrs.authorizationStatus.${value}`);
        case "startAt":
        case "stopAt":
          return formatDateTime(new Date(value));
        default:
          return value;
      }
    },
    [t],
  );

  const keysToTranslate = useMemo(
    () =>
      [
        "authorizationStatus",
        "startAt",
        "stopAt",
      ] as (keyof PublicEmspCdrsDisplayed)[],
    [],
  );

  const allCdrsDisplayed: PublicEmspCdrsDisplayed[] = useMemo(() => {
    if (!emspCdrsList) return [];
    const listSorted = sortByDateTime(emspCdrsList, "desc", "startAt");

    let rowsWithId = listSorted.map((av) => {
      const newDict: PublicEmspCdrsDisplayed & {
        id: string;
      } = {
        ...av,
        id: av.uuid,
      };
      return newDict;
    });

    keysToTranslate.forEach((key) => {
      rowsWithId = rowsWithId.map((row) => {
        return {
          ...row,
          [key]: findKeysToTranslate(
            key as keyof PublicEmspCdrs,
            row[key] as string,
          ),
        };
      });
    });

    return rowsWithId;
  }, [emspCdrsList, findKeysToTranslate, keysToTranslate]);

  useEffect(() => {
    if (allCdrsDisplayed && emspCdrsListStatus === "success") {
      setListDisplayed(allCdrsDisplayed);
    }
  }, [allCdrsDisplayed, emspCdrsListStatus]);

  useEffect(() => {
    if (params.id) {
      const cdrsSelected = (allCdrsDisplayed as PublicEmspCdrs[]).find(
        (t) => t.uuid === params.id,
      );
      if (cdrsSelected) {
        const firstPath = location.pathname.split("/")[1]; // home or departments
        navigate(`/${firstPath}/transactions/emspCdrs/${cdrsSelected.uuid}`);
      }
    }
  }, [allCdrsDisplayed, navigate, params.id]);

  const searchBarProps = {
    values: allCdrsDisplayed,
    setFilterValues: setListDisplayed,
    keysToIgnore: [
      "uuid" as const,
      "currency" as const,
      "vehicleUuid" as const,
      "cardUuid" as const,
      "currency" as const,
    ],
  };

  const columnsDisplayed: {
    [index in keyof PublicEmspCdrsDisplayed]: MaterialUiColumnsProps;
  } = useMemo(() => {
    return {
      registrationNumber: {
        type: "string",
        width: 200,
      },
      identifier: {
        type: "string",
        width: 200,
      },
      vehicleReference: {
        type: "string",
        width: 200,
      },
      driverName: {
        type: "string",
        width: 250,
        valueFormatter: (value: string, row) => {
          if (!value) return <>-</>;
          const initials = value?.split(" ").map((row) => row.charAt(0));
          return (
            value && (
              <StyledRow>
                <Avatar
                  color={"mint"}
                  size={"S"}
                  firstName={initials[0]}
                  lastName={initials[initials.length - 1]}
                  isHover={rowHoverUuid === row["uuid"]}
                />
                <Spacer x={0.75} />
                {value}
              </StyledRow>
            )
          );
        },
      },
      startAt: {
        type: "date",
        width: 200,
        valueGetter: getDateTime,
        valueFormatter: formatDateTime,
      },
      stopAt: {
        type: "date",
        width: 200,
        valueGetter: getDateTime,
        valueFormatter: formatDateTime,
      },
      locationCity: {
        type: "string",
        width: 200,
      },
      authorizationStatus: {
        type: "string",
        width: 200,
        renderCell: (p) =>
          p.id !== GRID_AGGREGATION_ROOT_FOOTER_ROW_ID ? (
            <StyledCell>
              <Tag
                text={p.value as string}
                backgroundColor={
                  authorizationStatusProps[
                    p.value as keyof typeof authorizationStatusProps
                  ].backgroundColor
                }
                textColor={
                  authorizationStatusProps[
                    p.value as keyof typeof authorizationStatusProps
                  ].textColor
                }
              />
            </StyledCell>
          ) : (
            <></>
          ),
      },
      totalCost: {
        type: "number",
        width: 160,
        valueFormatter: formatCurrency,
      },
      totalTime: {
        type: "number",
        width: 160,
        valueFormatter: formatHour,
      },
      totalEnergy: {
        type: "number",
        width: 200,
      },
      chargingTime: {
        type: "number",
        width: 160,
        valueFormatter: formatHour,
      },
      totalParkingTime: {
        type: "number",
        width: 190,
        valueFormatter: formatHour,
      },
    };
  }, [rowHoverUuid]);

  const downloadViewAction = useCallback(
    (file: Buffer | undefined) => {
      if (!file) return;
      void dispatch(
        documentDownloadXlsxAsync({
          buffer: file,
          fileName: "QIVIA_" + t("emspCdrs.title") + ".xlsx",
        }),
      );
    },
    [t, dispatch],
  );

  if (!listDisplayed) return <LaunchPage hasBorderRadius={true} />;

  return (
    <>
      <MaterialUiTable<keyof PublicEmspCdrsDisplayed, PublicEmspCdrsDisplayed>
        rows={listDisplayed}
        columns={columnsDisplayed}
        pageName={pageName}
        onRowClickAction={props.sidePanelAction}
        setRowHoverUuid={setRowHoverUuid}
        dataGridState={dataGridState}
        searchBar={searchBarProps}
        apiRef={apiRef}
        downloadViewAction={downloadViewAction}
      />
      {props.sidePanel ? props.sidePanel : <></>}
    </>
  );
};

const StyledCell = styled.div`
  height: 100%;
  display: flex;
  align-items: center;
`;

const StyledRow = styled.div`
  display: flex;
  align-items: center;
  height: 100%;
`;
